/**
 * Issue #43 — lecture des erreurs Entra au retour de redirection.
 *
 * Logique PURE, sans dépendance MSAL ni React : c'est elle qui décide ce que l'écran d'erreur
 * affiche, et c'est elle qui est testée. La garde anti-boucle, elle, n'est PAS ici — elle est
 * portée par `useMsalAuthentication` (ref `shouldAcquireToken`, arrêt dès qu'une erreur est posée).
 * Cf. Docs/interne/pilotage/suivi/echanges/historique/2026-08-29_1830_question_43-approche-v2-mecanismes-msal.md.
 */

/**
 * Signal **stable** d'un consentement manquant : le code d'erreur OAuth 2.0 porté par MSAL.
 *
 * ⚠️ Hiérarchie voulue, et elle compte. Microsoft écrit, dans sa référence des codes AADSTS :
 * « This documentation is provided for developer and admin guidance, but **should never be used by
 * the client itself**. Error codes are subject to change at any time […] Apps that take a dependency
 * on text or error code numbers **will be broken over time**. »
 * ⇒ La logique s'appuie d'abord sur ces codes OAuth (et sur le TYPE de l'erreur MSAL) ; les numéros
 * AADSTS ne servent qu'en **repli** et à l'affichage.
 *
 * ⚠️ `invalid_grant` a été retiré de cet ensemble (audit de clôture, 2026-08-29). Il **n'est pas** un
 * code « interaction requise » dans `@azure/msal-common`, et Entra le renvoie couramment pour un
 * refresh token expiré ou révoqué (`AADSTS700082`, `AADSTS50173`) : le retenir aurait servi
 * « Autorisation requise dans votre annuaire » — et un lien de consentement inutile — à un
 * utilisateur dont le tenant a **déjà** consenti. C'est le contresens même que ce module s'interdit.
 * Le cas légitime où `invalid_grant` accompagne un consentement manquant (`AADSTS65001`) reste
 * couvert : il est attrapé par le repli AADSTS ci-dessous.
 */
const CONSENT_OAUTH_CODES = new Set(['consent_required'])

/**
 * Repli **best-effort**, explicitement fragile : numéros AADSTS observés pour un consentement
 * administrateur manquant. Ne jamais en faire l'unique condition — s'ils cessent de correspondre,
 * l'écran retombe simplement sur le message générique, sans lien de consentement. C'est une
 * dégradation acceptable, pas une panne.
 */
const CONSENT_AADSTS_FALLBACK = new Set(['AADSTS650052', 'AADSTS650051', 'AADSTS65001'])

const AADSTS_RE = /\bAADSTS\d{4,6}\b/
// `AADSTS650052` existe en **au moins deux formulations**, toutes deux rencontrées en vrai :
//   « ... was not found in the tenant named <guid> ... »              (relevé 2026-08-19)
//   « ... that your organization '<guid>' lacks a service principal » (relevé 2026-08-29, dev)
// D'où l'alternance `tenant|organization`.
//
// ⚠️ L'ancrage sur un mot-clé n'est pas une précaution théorique : le message réel contient
// **quatre** GUID, dont trois leurres — l'appId de l'API (« a service '<appId>' »), le Trace ID et
// le Correlation ID. Un motif « premier GUID venu » construirait un lien de consentement pointant
// vers un annuaire inexistant.
//
// Reste **best-effort**, conformément à l'avertissement de Microsoft sur la dépendance au texte :
// si aucune forme ne correspond, `buildAdminConsentUrl` retombe sur `organizations`, qui fonctionne.
const TENANT_RE =
  /(?:tenant|organization)\s+(?:named\s+)?'?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

/** Le code AADSTS, tel quel — c'est lui qu'un client nous transmettra. `null` si absent. */
export function parseAadstsCode(message: string | undefined | null): string | null {
  if (!message) return null
  return AADSTS_RE.exec(message)?.[0] ?? null
}

/** Ce que MSAL nous dit d'une erreur, sans dépendre de son type concret (donc testable à nu). */
export interface AuthErrorSignals {
  /** `AuthError.errorCode` — code OAuth 2.0 ou identifiant MSAL. Stable. */
  errorCode?: string | undefined
  /** `AuthError.errorMessage` — texte Microsoft. **Diagnostic uniquement**, jamais une dépendance. */
  errorMessage?: string | undefined
  /** `error instanceof InteractionRequiredAuthError` — signal de type, stable. */
  interactionRequired?: boolean | undefined
}

/**
 * `true` si l'action qui débloque appartient au client (consentement admin), et non à nous.
 *
 * Ordre : code OAuth d'abord (stable), numéro AADSTS ensuite (repli fragile, cf. commentaires
 * ci-dessus). Le drapeau `interactionRequired` seul ne suffit PAS : une session expirée le porte
 * aussi, et proposer un lien de consentement à quelqu'un dont le tenant a déjà consenti serait
 * trompeur.
 */
export function isConsentError(signals: AuthErrorSignals): boolean {
  const oauth = signals.errorCode?.toLowerCase()
  if (oauth && CONSENT_OAUTH_CODES.has(oauth)) return true
  const aadsts = parseAadstsCode(signals.errorMessage)
  return aadsts !== null && CONSENT_AADSTS_FALLBACK.has(aadsts)
}

/**
 * Identifiant de tenant lu dans le message d'erreur. Le portail ne le connaît pas autrement :
 * `authority` vaut `.../organizations`, donc aucun tenant n'est résolu avant un login réussi.
 */
export function parseTenantId(message: string | undefined | null): string | null {
  if (!message) return null
  return TENANT_RE.exec(message)?.[1]?.toLowerCase() ?? null
}

/**
 * Lien de consentement administrateur.
 *
 * La divergence signalée par la gate `shared-contract-guardrail` du 2026-08-29 est **résorbée ici** :
 * ce lien porte désormais `redirect_uri` + `state`, comme la forme canonique de
 * `Docs/platform/architecture/AUTH-multitenant.md`. Elle n'était pas bénigne — le test T1.d l'a montré le même jour.
 * Reste une troisième forme, sans `redirect_uri`, dans
 * `components/reseller/onboarding/OnboardingAuthStep.tsx` (app d'audit revendeur, autre appId) :
 * à vérifier lorsqu'on y touchera, elle expose au même symptôme.
 *
 * Le `clientId` attendu est celui de l'app **SPA** : seul `/adminconsent` sur elle provoque le
 * consentement groupé des deux apps via `knownClientApplications` (cf. `AUTH-multitenant.md`).
 *
 * À défaut de tenant identifié, on retombe sur
 * `organizations` : Microsoft demandera alors son annuaire à l'administrateur, ce qui vaut
 * infiniment mieux qu'un lien pointant sur un tenant deviné.
 */
export function buildAdminConsentUrl(
  tenantId: string | null,
  clientId: string,
  redirectUri?: string | undefined
): string {
  const tenant = tenantId ?? 'organizations'
  const base = `https://login.microsoftonline.com/${tenant}/adminconsent?client_id=${clientId}`
  // `redirect_uri` n'est pas cosmétique : SANS lui, Entra renvoie l'administrateur sur l'URI de
  // redirection **par défaut** de l'app registration — qui est `http://localhost:5173/auth/callback`,
  // l'adresse du développement local. Constaté le 2026-08-29 en test T1.d : après avoir consenti,
  // l'administrateur atterrissait sur une page morte. On renvoie donc vers le portail d'où il vient.
  return redirectUri ? `${base}&redirect_uri=${encodeURIComponent(redirectUri)}&state=onboarding` : base
}

export interface AuthErrorView {
  /** Code AADSTS brut, toujours affiché quand il existe. */
  code: string | null
  /** Message d'Entra, conservé tel quel — c'est le diagnostic. */
  detail: string
  /** Titre et explication destinés à l'utilisateur bloqué. */
  title: string
  explanation: string
  /** Renseigné uniquement quand l'action appartient au client (consentement admin). */
  adminConsentUrl: string | null
}

/**
 * Traduit une erreur d'authentification en ce que l'écran doit montrer.
 *
 * Deux niveaux, jamais un message générique :
 *  - action nommée quand le déblocage appartient au client (consentement admin) ;
 *  - affichage lisible sinon — `AADSTS50011` (redirect URI) ou `AADSTS650053` (scope) sont des
 *    défauts de NOTRE configuration : les nommer nous fait gagner le diagnostic, leur inventer
 *    une action côté client serait trompeur.
 */
export function describeAuthError(
  signals: AuthErrorSignals,
  clientId: string,
  /** Où renvoyer l'administrateur après son consentement — l'origine du portail courant. */
  redirectUri?: string | undefined
): AuthErrorView {
  const message = signals.errorMessage
  // Le numéro AADSTS est **affiché**, jamais décisif : c'est lui qu'un client nous citera et qui a
  // permis le diagnostic du 2026-08-19, mais Microsoft se réserve de le changer.
  const code = parseAadstsCode(message) ?? signals.errorCode ?? null
  const detail = (message ?? '').trim()

  if (isConsentError(signals)) {
    return {
      code,
      detail,
      title: 'Autorisation requise dans votre annuaire',
      explanation:
        "L'accès au portail doit être autorisé une fois par un administrateur général de votre " +
        'organisation. Transmettez-lui le lien ci-dessous : la validation prend quelques secondes, ' +
        'et vous pourrez ensuite vous connecter normalement.',
      adminConsentUrl: buildAdminConsentUrl(parseTenantId(message), clientId, redirectUri),
    }
  }

  // Interaction requise sans consentement manquant = session à renouveler : réessayer a de bonnes
  // chances d'aboutir, et c'est ce qu'il faut dire plutôt que d'envoyer l'utilisateur au support.
  if (signals.interactionRequired) {
    return {
      code,
      detail,
      title: 'Votre session a expiré',
      explanation:
        'Microsoft Entra demande une nouvelle authentification. Cliquez sur « Réessayer » pour vous ' +
        "reconnecter ; aucune action particulière n'est requise de votre part.",
      adminConsentUrl: null,
    }
  }

  return {
    code,
    detail,
    title: 'Connexion impossible',
    explanation:
      "La connexion a été refusée par Microsoft Entra. Ce n'est pas une action à mener de votre " +
      'côté : transmettez le code ci-dessous au support Pragmelis, il identifie précisément la cause.',
    adminConsentUrl: null,
  }
}
